<script setup>
import { ref, onBeforeUnmount, nextTick, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useEventListener } from '@vueuse/core';

// Componentes
import CampaignDetailsModal from './CampaignDetailsModal.vue';
import DeleteConfirmationModal from './DeleteConfirmationModal.vue';

// Hooks
import { useCampaignData } from './hooks/useCampaignData.js';
import { useDeleteActions } from './hooks/useDeleteActions.js';
import { useCampaignStatusSync } from './hooks/useCampaignStatusSync.ts';
import AudiencePreviewModal from 'dashboard/routes/dashboard/campaigns/pages/whatsapp_app/components/AudiencePreviewModal.vue';
import BaylesCampaignDetailsModal from 'dashboard/routes/dashboard/campaigns/pages/whatsapp_app/components/BaylesCampaignDetailsModal.vue';

const props = defineProps({
  row: { type: Object, required: true },
});

const emit = defineEmits(['deleteSchedule', 'deleteCampaign']);


const router = useRouter();
const route = useRoute();

// Estados locais
const dropdownOpen = ref(false);
const isDetailsModalOpen = ref(false);
const isComponentMounted = ref(true);
const triggerButtonRef = ref(null);
const isAudiencePreviewModalOpen = ref(false);
const isBayleysDetailsModalOpen = ref(false);


// Refs para posicionamento do dropdown
const dropdownPosition = ref({
  top: '0px',
  left: '0px',
  position: 'below',
});

// Ref reativo para a linha
const rowRef = ref(props.row);

// Watch para atualizar rowRef quando props.row mudar
watch(() => props.row, (newRow) => {
  if (isComponentMounted.value && newRow) {
    rowRef.value = newRow;
  }
}, { immediate: true });

// Hooks existentes
const {
  rowInfo,
  campaignData,
  highlightPlaceholders,
  translateOperator,
  formatPlaceholder,
  getCharacterCount,
  generateSQLPreview
} = useCampaignData(rowRef);

const {
  isDeleteModalOpen,
  deleteType,
  handleDeleteSchedule,
  handleDeleteCampaign,
  confirmDelete,
  cancelDelete
} = useDeleteActions(emit);

// ✅ NOVO: Hook para sincronização de status
const {
  syncedStatus,
  canAccessBroadcastList,
  isRunning,
  isCompleted,
  isEmptyCampaign
} = useCampaignStatusSync(
  rowInfo.value?.scheduleId || 0,
  props.row?.original?.status || props.row?.original?.campaign_status
);

// ✅ NOVO: Verificar se pode deletar (apenas agendamentos com status "scheduled")
const canDelete = computed(() => {
  // syncedStatus é enum CRU
  return syncedStatus.value === 'scheduled';
});

// ✅ NOVO: Tooltip para botões de delete
const deleteTooltip = computed(() => {
  if (canDelete.value) {
    return {
      schedule: 'Remove apenas este agendamento',
      campaign: 'Remove a campanha e todos os agendamentos pendentes'
    };
  }

  if (isRunning.value) {
    return {
      schedule: 'Não é possível deletar - campanha em execução',
      campaign: 'Não é possível deletar - campanha em execução'
    };
  }

  if (isCompleted.value) {
    return {
      schedule: 'Não é possível deletar - agendamento já foi executado',
      campaign: 'Não é possível deletar - campanha já foi executada'
    };
  }

  return {
    schedule: 'Não é possível deletar este agendamento',
    campaign: 'Não é possível deletar esta campanha'
  };
});

// Detectar se é campanha Baileys
const isBaylesCampaign = computed(() => {
  const ct = props.row?.original?.campaign_type;
  return ct === 'whatsapp_bayles';
});

// Dados formatados para o modal Baileys
const baylesCampaignData = computed(() => {
  const data = props.row?.original;
  if (!data) return {};
  const [campaignId, scheduleId] = data.id.split('-');
  const rawAudience = data.audience;
  let parsedAudience = null;
  try {
    parsedAudience = typeof rawAudience === 'string' ? JSON.parse(rawAudience) : rawAudience;
  } catch {
    parsedAudience = null;
  }
  // Parse filter_data e template_params (vêm stringificados do flattenCampaigns)
  let parsedFilterData = null;
  try {
    parsedFilterData = typeof data.filter_data === 'string' ? JSON.parse(data.filter_data) : data.filter_data;
  } catch {
    parsedFilterData = null;
  }
  let parsedTemplateParams = null;
  try {
    parsedTemplateParams = typeof data.template_params === 'string' ? JSON.parse(data.template_params) : data.template_params;
  } catch {
    parsedTemplateParams = null;
  }

  return {
    campaignName: data.title || '',
    message: data.message || '',
    hasMessage: !!data.message,
    audience: parsedAudience,
    campaignStatus: data.campaign_status || '',
    progressPercentage: data.progress_percentage || 0,
    totalItems: data.total_items || 0,
    itemsSent: data.items_sent || 0,
    itemsFailed: data.items_failed || 0,
    scheduledAt: data.scheduled_at || '',
    statistics: data.statistics || null,
    statusMessage: data.status_message || '',
    campaignId: parseInt(campaignId),
    scheduleId: parseInt(scheduleId),
    filterData: parsedFilterData,
    templateParams: parsedTemplateParams,
  };
});

// Handler para abrir modal Baileys
const handleOpenBayleysDetails = async () => {
  if (!isComponentMounted.value) return;
  await nextTick();
  if (isComponentMounted.value) {
    isBayleysDetailsModalOpen.value = true;
    closeDropdown();
  }
};

// Handler para abrir Lista de Transmissão Baileys
const handleOpenBaylesBroadcastList = () => {
  if (!isComponentMounted.value || !canAccessBroadcastList.value) return;

  const scheduling_id = rowInfo.value?.scheduleId;
  const campaign_id = rowInfo.value?.campaignId;

  closeDropdown();

  nextTick(() => {
    router.push({
      name: 'campaigns_whatsapp_app_schedule_deliveries',
      params: {
        accountId: route.params.accountId,
        scheduling_id: scheduling_id,
      },
      query: {
        campaignId: campaign_id,
      },
    });
  });
};

useEventListener(window, 'resize', () => {
  if (dropdownOpen.value) {
    calculateDropdownPosition();
  }
});

// ✅ MELHORADO: Tooltip mais informativo para a versão compacta
const broadcastListTooltip = computed(() => {
  if (canAccessBroadcastList.value) {
    return 'Ver lista de transmissão (Disponível)';
  }
  
  if (isEmptyCampaign.value) {
    return 'Lista de transmissão - Nenhum dado para exibir (campanha sem registros)';
  }
  
  if (isRunning.value) {
    return 'Lista de transmissão - Aguardando conclusão da campanha';
  }
  
  return 'Lista de transmissão - Disponível apenas para campanhas concluídas ou canceladas';
});

// Função para calcular posição do dropdown
const calculateDropdownPosition = () => {
  if (!triggerButtonRef.value) return;

  const rect = triggerButtonRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth; // ✅ CORRIGIDO: window.width → window.innerWidth
  const dropdownHeight = 250; // altura estimada do dropdown
  const dropdownWidth = 192; // largura estimada (w-48 = 12rem = 192px)

  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = viewportWidth - rect.right;
  const spaceLeft = rect.left;

  // Determinar posição vertical (acima ou abaixo)
  const position = spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'above' : 'below';
  
  // Determinar posição horizontal (esquerda ou direita)
  const horizontalPosition = spaceRight < dropdownWidth && spaceLeft > dropdownWidth ? 'left' : 'right';

  dropdownPosition.value = {
    top: position === 'above' 
      ? `${rect.top - dropdownHeight + window.scrollY}px`
      : `${rect.bottom + window.scrollY + 8}px`,
    left: horizontalPosition === 'left'
      ? `${rect.left - dropdownWidth + rect.width}px`
      : `${rect.left}px`,
    position
  };
};

// Função para abrir dropdown
const openDropdown = async () => {
  if (!isComponentMounted.value) return;

  dropdownOpen.value = true;
  await nextTick();
  calculateDropdownPosition();
};

// Funções para controlar dropdown
const toggleDropdown = async () => {
  if (!isComponentMounted.value) return;
  
  if (dropdownOpen.value) {
    closeDropdown();
  } else {
    await openDropdown();
  }
};

const closeDropdown = () => {
  if (!isComponentMounted.value) return;
  dropdownOpen.value = false;
};

// Função para abrir Lista de Transmissão
const handleOpenBroadcastList = () => {
  if (!isComponentMounted.value || !canAccessBroadcastList.value) return;

  const scheduling_id = rowInfo.value?.scheduleId;
  const campaign_id = rowInfo.value?.campaignId;

  closeDropdown();

  nextTick(() => {
    router.push({
      name: 'campaigns_whatsapp_official_schedule_deliveries',
      params: {
        accountId: route.params.accountId,
        scheduling_id: scheduling_id,
      },
      query: {
        campaignId: campaign_id,
      },
    });
  });
};

const handleOpenAudiencePreview = async () => {
  if (!isComponentMounted.value) return;

  await nextTick();
  if (isComponentMounted.value) {
    isAudiencePreviewModalOpen.value = true;
    
    closeDropdown();
  }
};

// Watch para mudanças de rota
watch(() => route.path, (newPath, oldPath) => {
  if (newPath !== oldPath) {
    closeDropdown();
    isDetailsModalOpen.value = false;
    isAudiencePreviewModalOpen.value = false;
    isBayleysDetailsModalOpen.value = false;
  }
});

// Função para abrir modal de detalhes
const handleOpenCampaignDetails = async () => {
  if (!isComponentMounted.value) return;

  await nextTick();
  if (isComponentMounted.value) {
    isDetailsModalOpen.value = true;
    closeDropdown();
  }
};

// Função para confirmar delete
const handleConfirmDelete = () => {
  if (!isComponentMounted.value) return;
  confirmDelete(rowInfo.value);
};

// Função para lidar com delete de agendamento
const handleDeleteScheduleAction = () => {
  if (!isComponentMounted.value) return;
  handleDeleteSchedule();
  closeDropdown();
};

// Função para lidar com delete de campanha
const handleDeleteCampaignAction = () => {
  if (!isComponentMounted.value) return;
  handleDeleteCampaign();
  closeDropdown();
};

// Cleanup ao desmontar
onBeforeUnmount(() => {
  isComponentMounted.value = false;
  dropdownOpen.value = false;
  isDetailsModalOpen.value = false;
  isAudiencePreviewModalOpen.value = false;
  isBayleysDetailsModalOpen.value = false;
  rowRef.value = null;
});
</script>

<template>
  <!-- Dropdown Menu -->
  <div class="relative inline-block" v-if="isComponentMounted">
    <!-- ✅ MELHORADO: Mostrar mini status badge no trigger button -->
    <button
      ref="triggerButtonRef"
      @click="toggleDropdown"
      class="flex h-8 w-8 p-0 items-center justify-center rounded hover:bg-green-100 dark:hover:bg-green-800 transition-colors relative"
    >
      
      <span class="i-lucide-more-horizontal h-4 w-4"></span>
      <span class="sr-only">Open menu</span>
    </button>
  </div>

  <!-- Dropdown Content via Teleport -->
  <Teleport to="body">
    <Transition
      name="dropdown-fade"
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="dropdownOpen && isComponentMounted"
        :style="dropdownPosition"
        class="w-48 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden absolute z-50"
        @click.stop
      >
        <!-- Backdrop invisível para fechar dropdown -->
        <div
          class="fixed inset-0 z-[-1]"
          @click="closeDropdown"
        ></div>

        <div class="py-1">

          <!-- ========================================== -->
          <!-- Channel::Whatsapp + Baileys (campaign_type = whatsapp_bayles) -->
          <!-- ========================================== -->

          <!-- Ver Detalhes — Baileys -->
          <button
            v-if="props.row?.original?.channel_type === 'Channel::Whatsapp' && isBaylesCampaign"
            @click="handleOpenBayleysDetails"
            class="flex w-full items-center px-3 py-2 text-sm text-green-700 dark:text-slate-300 hover:bg-green-100 dark:hover:bg-green-800 transition-colors"
          >
            <span class="i-lucide-eye mr-2 h-4 w-4 flex-shrink-0"></span>
            Ver Detalhes
          </button>

          <!-- Lista Transmissão — Baileys -->
          <button
            v-if="props.row?.original?.channel_type === 'Channel::Whatsapp' && isBaylesCampaign"
            @click="handleOpenBaylesBroadcastList"
            :disabled="!canAccessBroadcastList"
            :title="broadcastListTooltip"
            class="flex w-full items-center justify-between px-3 py-2 text-sm transition-colors"
            :class="canAccessBroadcastList
              ? 'text-green-700 dark:text-slate-300 hover:bg-green-100 dark:hover:bg-green-800 cursor-pointer'
              : 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-60'"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-radio h-4 w-4 flex-shrink-0"></span>
              <span>Lista Transmissão</span>
            </div>
            <div class="flex items-center gap-1">
              <svg v-if="isRunning" class="animate-spin h-3 w-3 text-yellow-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div
                class="w-2 h-2 rounded-full"
                :class="{
                  'bg-yellow-500': isRunning,
                  'bg-green-500': canAccessBroadcastList && !isRunning,
                  'bg-slate-400': !canAccessBroadcastList && !isRunning
                }"
              ></div>
            </div>
          </button>

          <!-- ========================================== -->
          <!-- Channel::Whatsapp + Oficial (NÃO baileys) -->
          <!-- ========================================== -->

          <!-- Ver Detalhes — Oficial -->
          <button
            v-if="props.row?.original?.channel_type === 'Channel::Whatsapp' && !isBaylesCampaign"
            @click="handleOpenCampaignDetails"
            class="flex w-full items-center px-3 py-2 text-sm text-green-700 dark:text-slate-300 hover:bg-green-100 dark:hover:bg-green-800 transition-colors"
          >
            <span class="i-lucide-eye mr-2 h-4 w-4 flex-shrink-0"></span>
            Ver Detalhes
          </button>

          <!-- Lista Transmissão — Oficial -->
          <button
            v-if="props.row?.original?.channel_type === 'Channel::Whatsapp' && !isBaylesCampaign"
            @click="handleOpenBroadcastList"
            :disabled="!canAccessBroadcastList"
            :title="broadcastListTooltip"
            class="flex w-full items-center justify-between px-3 py-2 text-sm transition-colors"
            :class="canAccessBroadcastList
              ? 'text-green-700 dark:text-slate-300 hover:bg-green-100 dark:hover:bg-green-800 cursor-pointer'
              : 'text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-60'"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-radio h-4 w-4 flex-shrink-0"></span>
              <span>Lista Transmissão</span>
            </div>
            <div class="flex items-center gap-1">
              <svg v-if="isRunning" class="animate-spin h-3 w-3 text-yellow-500" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <div
                class="w-2 h-2 rounded-full"
                :class="{
                  'bg-yellow-500': isRunning,
                  'bg-green-500': canAccessBroadcastList && !isRunning,
                  'bg-slate-400': !canAccessBroadcastList && !isRunning
                }"
              ></div>
            </div>
          </button>

          <!-- ========================================== -->
          <!-- Channel::Api — Baileys tag campaign -->
          <!-- ========================================== -->

          <!-- Ver Detalhes — Channel::Api -->
          <button
            v-if="props.row?.original?.channel_type === 'Channel::Api'"
            @click="handleOpenBayleysDetails"
            class="flex w-full items-center px-3 py-2 text-sm text-green-700 dark:text-slate-300 hover:bg-green-100 dark:hover:bg-green-800 transition-colors"
          >
            <span class="i-lucide-eye mr-2 h-4 w-4 flex-shrink-0"></span>
            Ver Detalhes
          </button>

          <!-- Lista Transmissão — Channel::Api -->
          <button
            v-if="props.row?.original?.channel_type === 'Channel::Api'"
            @click="handleOpenAudiencePreview"
            class="flex w-full items-center px-3 py-2 text-sm text-green-700 dark:text-slate-300 hover:bg-green-100 dark:hover:bg-green-800 transition-colors"
          >
            <span class="i-lucide-users mr-2 h-4 w-4 flex-shrink-0"></span>
            Lista de Transmissão
          </button>

          <!-- Separador antes da seção de exclusão -->
          <hr v-if="props.row?.original?.channel_type === 'Channel::Whatsapp' || props.row?.original?.channel_type === 'Channel::Api'"
              class="my-1 border-slate-200 dark:border-slate-700">

          <!-- Seção de Exclusão -->
          <div class="px-3 py-2">
            <!-- Aviso quando não pode deletar -->
            <div v-if="!canDelete" class="mb-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <span class="i-lucide-info h-3 w-3 flex-shrink-0"></span>
              <span v-if="isRunning">Campanha em execução</span>
              <span v-else>Agendamento já executado</span>
            </div>

            <!-- Container dos botões lado a lado -->
            <div class="flex gap-2">
              <!-- Deletar apenas este agendamento -->
              <button
                @click="handleDeleteScheduleAction"
                :disabled="!canDelete"
                class="flex-1 flex items-center justify-center px-3 py-2 bg-white dark:bg-slate-800 border rounded-md transition-colors"
                :class="canDelete
                  ? 'border-orange-300 dark:border-orange-600 text-orange-600 dark:text-orange-400 hover:bg-yellow-100 dark:hover:bg-yellow-500/30 cursor-pointer'
                  : 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'"
                v-tooltip.right="deleteTooltip.schedule"
              >
                <span class="i-lucide-calendar-x h-4 w-4" :class="canDelete ? 'text-orange-600 dark:text-yellow-100' : 'text-slate-400'"></span>
              </button>

              <!-- Deletar campanha inteira -->
              <button
                @click="handleDeleteCampaignAction"
                :disabled="!canDelete"
                class="flex-1 flex items-center justify-center px-3 py-2 bg-white dark:bg-slate-800 border rounded-md transition-colors"
                :class="canDelete
                  ? 'border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 cursor-pointer'
                  : 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-600 cursor-not-allowed opacity-50'"
                v-tooltip.right="deleteTooltip.campaign"
              >
                <span class="i-lucide-trash-2 h-4 w-4" :class="canDelete ? 'text-red-600 dark:text-red-400' : 'text-slate-400'"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Modal de Detalhes da Campanha -->
  <CampaignDetailsModal
    v-if="isDetailsModalOpen && isComponentMounted"
    :is-open="isDetailsModalOpen"
    :campaign-data="campaignData"
    :highlight-placeholders="highlightPlaceholders || (() => '')"
    :translate-operator="translateOperator || ((op) => op)"
    :format-placeholder="formatPlaceholder || ((key) => `{${key}}`)"
    :get-character-count="getCharacterCount || (() => ({ characters: 0, isSMSLength: false, isWhatsAppOptimal: false }))"
    :generate-sql-preview="generateSQLPreview || (() => 'SQL Preview não disponível')"
    @close="isDetailsModalOpen = false"
  />

  <!-- Modal Baileys Details -->
  <BaylesCampaignDetailsModal
    v-if="isBayleysDetailsModalOpen && isComponentMounted"
    :is-open="isBayleysDetailsModalOpen"
    :campaign-data="baylesCampaignData"
    @close="isBayleysDetailsModalOpen = false"
  />

    <!-- ✅ NOVO: Modal de Preview da Audiência - API -->
  <AudiencePreviewModal
    v-if="isAudiencePreviewModalOpen && isComponentMounted"
    :is-open="isAudiencePreviewModalOpen"
    :campaign-data="campaignData"
    @close="isAudiencePreviewModalOpen = false"
  />


  <!-- Modal de Confirmação de Delete -->
  <DeleteConfirmationModal
    v-if="isDeleteModalOpen && isComponentMounted"
    :is-open="isDeleteModalOpen"
    :delete-type="deleteType"
    :row-info="rowInfo"
    @confirm="handleConfirmDelete"
    @cancel="cancelDelete"
  />
</template>

<style scoped>
/* Animações do dropdown */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Garantir que botões não quebrem layout */
button {
  transition: all 0.2s ease;
}

/* Spinner animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Responsividade para mobile */
@media (max-width: 768px) {
  :deep(.w-48) {
    position: fixed !important;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: 90vw !important;
    max-width: 300px !important;
  }
}

/* Estados de foco melhorados */
button:focus-visible {
  @apply ring-2 ring-woot-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900;
}

/* Garantir que ícones não encolham */
.flex-shrink-0 {
  flex-shrink: 0;
}

/* Disabled state styles */
button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

button:disabled:hover {
  background-color: transparent !important;
}
</style>